class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    this.startStockCheck();
    window.needohCartLineProofRefresh?.();
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.cancelStockCheck();
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  disconnectedCallback() {
    this.cancelStockCheck();
  }

  startStockCheck(restart = false) {
    if (!this.querySelector('[data-stock-variant-id]') || this.classList.contains('is-empty')) {
      this.cancelStockCheck();
      return;
    }
    if (this.stockCheckRun && !restart) return this.stockCheckRun.promise;
    this.cancelStockCheck();

    const run = { controller: new AbortController() };
    this.stockCheckRun = run;
    this.classList.add('is-checking-stock');
    this.querySelector('.drawer__cart-items-wrapper')?.setAttribute('aria-busy', 'true');
    const message = this.querySelector('[data-stock-message]');
    if (message) {
      message.textContent = '';
      message.hidden = true;
    }

    // Keep the reveal at three seconds, even if an availability request stalls.
    const minimumDisplay = new Promise((resolve) => {
      run.delayId = setTimeout(resolve, 3000);
      run.cancelDelay = resolve;
    });
    const requestTimeout = new Promise((resolve) => {
      run.timeoutId = setTimeout(() => {
        run.controller.abort();
        resolve(null);
      }, 2800);
    });
    const availability = Promise.race([this.checkStockAvailability(run.controller.signal), requestTimeout])
      .catch(() => null);

    run.promise = Promise.all([minimumDisplay, availability]).then(([, unavailable]) => {
      if (this.stockCheckRun !== run) return;
      this.cancelStockCheck();
      const currentMessage = this.querySelector('[data-stock-message]');
      if (!currentMessage) return;
      currentMessage.textContent = unavailable === null
        ? 'Stock will be confirmed at checkout.'
        : unavailable.length ? 'An item may no longer be available. Please review your cart.' : '';
      currentMessage.hidden = !currentMessage.textContent;
    });
    return run.promise;
  }

  async checkStockAvailability(signal) {
    const items = Array.from(this.querySelectorAll('[data-stock-variant-id]'), (item) => ({
      handle: item.dataset.stockProductHandle,
      variantId: item.dataset.stockVariantId,
    }));
    const root = window.Shopify?.routes?.root || routes.cart_url.replace(/cart\/?$/, '');
    const products = new Map();
    for (const { handle } of items) {
      if (!handle) throw new Error('Missing product handle');
      if (!products.has(handle)) {
        products.set(handle, fetch(`${root}products/${encodeURIComponent(handle)}.js`, {
          signal,
          credentials: 'same-origin',
          cache: 'no-store',
          headers: { Accept: 'application/json' },
        }).then((response) => {
          if (!response.ok) throw new Error('Unable to check product availability');
          return response.json();
        }));
      }
    }
    const unavailable = await Promise.all(items.map(async ({ handle, variantId }) => {
      const product = await products.get(handle);
      const variant = product.variants?.find((entry) => String(entry.id) === variantId);
      if (!variant || typeof variant.available !== 'boolean') throw new Error('Unknown variant availability');
      return variant.available ? null : variantId;
    }));
    return unavailable.filter(Boolean);
  }

  cancelStockCheck() {
    const run = this.stockCheckRun;
    this.stockCheckRun = null;
    if (run) {
      run.controller.abort();
      clearTimeout(run.delayId);
      clearTimeout(run.timeoutId);
      run.cancelDelay?.();
    }
    this.classList.remove('is-checking-stock');
    this.querySelector('.drawer__cart-items-wrapper')?.removeAttribute('aria-busy');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });
    window.needohCartLineProofRefresh?.();
    this.startStockCheck(true);

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

/* ------------------------------------------------------------------
 * Cart line badges
 * Shows one of: Almost Gone / Top Selling / Limited Stock / Last one /
 * Last one left — or no badge.
 *
 * Markup it looks for (inside each cart line):
 *   <span data-needoh-cart-line-proof-v2 data-seed="{{ item.key }}" hidden>
 *     <span data-needoh-cart-line-proof-text-v2></span>
 *   </span>
 * ------------------------------------------------------------------ */
(() => {
  if (window.needohCartLineProofInit) return;
  window.needohCartLineProofInit = true;

  const SELECTOR = '[data-needoh-cart-line-proof-v2]';
  const TEXT_SELECTOR = '[data-needoh-cart-line-proof-text-v2]';
  const BADGES = ['Almost Gone', 'Top Selling', 'Limited Stock', 'Last one', 'Last one left', null]; // null = no badge

  const STORAGE_KEY = 'needohCartLineBadgeState';

  const readState = () => {
    try {
      return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '{}') || {};
    } catch (error) {
      return {};
    }
  };

  const writeState = (state) => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (error) {
      // Session storage can be unavailable in some privacy modes.
    }
  };

  // In-memory copy so the badge stays the same even if sessionStorage is blocked
  const memoryState = readState();

  // Random badge per cart line, kept the same for the whole browsing session
  // (so it doesn't jump around when the drawer re-opens or quantities change)
  const pickBadge = (element, index) => {
    const seed = String(element.dataset.seed || `cart-line-${index}`);

    if (!(seed in memoryState)) {
      memoryState[seed] = Math.floor(Math.random() * BADGES.length);
      writeState(memoryState);
    }

    return BADGES[memoryState[seed]] ?? null;
  };

  /* --------------------------------------------------------------
   * Badge icons — one per badge. To use your own icon, replace the
   * <svg>...</svg> string for that badge. Keep stroke/fill as
   * "currentColor" so the icon matches the badge text color.
   * -------------------------------------------------------------- */
  const ICON_SELECTOR = '[data-needoh-badge-icon]';
  const SVG_OPEN =
    '<svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2.5" ' +
    'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">';

  const ICONS = {
    // Hourglass
    'almost-gone': `${SVG_OPEN}<path d="M6 2h12M6 22h12"/><path d="M7 2v3c0 2.5 2 4 5 7 3-3 5-4.5 5-7V2"/><path d="M7 22v-3c0-2.5 2-4 5-7 3 3 5 4.5 5 7v3"/></svg>`,
    // Flame
    'top-selling': `${SVG_OPEN}<path d="M12 2c1 3.5 5 6 5 11a5 5 0 0 1-10 0c0-2.5 1.5-4 2.5-5 .3 1.7 1 2.7 2.5 3 0-3-1-6 0-9z"/></svg>`,
    // Box
    'limited-stock': `${SVG_OPEN}<path d="M3 7l9-4 9 4-9 4-9-4z"/><path d="M3 7v10l9 4 9-4V7"/><path d="M12 11v10"/></svg>`,
    // Alert circle
    'last-one': `${SVG_OPEN}<circle cx="12" cy="12" r="9"/><path d="M12 7v6"/><path d="M12 16.5v.5"/></svg>`,
    // Clock
    'last-one-left': `${SVG_OPEN}<circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>`,
  };

  // Make sure each badge has a text span and an icon span (icon sits before the text)
  const ensureParts = (element) => {
    let textElement = element.querySelector(TEXT_SELECTOR);
    if (!textElement) {
      textElement = document.createElement('span');
      textElement.setAttribute('data-needoh-cart-line-proof-text-v2', '');
      element.textContent = '';
      element.appendChild(textElement);
    }

    let iconElement = element.querySelector(ICON_SELECTOR);
    if (!iconElement) {
      iconElement = document.createElement('span');
      iconElement.className = 'needoh-badge__icon';
      iconElement.setAttribute('data-needoh-badge-icon', '');
      iconElement.setAttribute('aria-hidden', 'true');
      textElement.before(iconElement);
    }

    return { textElement, iconElement };
  };

  // Only touch the DOM when something actually changes (keeps the observer below from looping)
  const applyBadge = (element, badge) => {
    const { textElement, iconElement } = ensureParts(element);
    const text = badge || '';
    const slug = badge ? badge.toLowerCase().replace(/\s+/g, '-') : '';

    if (textElement.textContent !== text) textElement.textContent = text;

    const iconSlug = slug && ICONS[slug] ? slug : '';
    if (iconElement.dataset.iconFor !== iconSlug) {
      iconElement.innerHTML = iconSlug ? ICONS[iconSlug] : '';
      iconElement.dataset.iconFor = iconSlug;
      iconElement.hidden = !iconSlug;
    }

    if (slug) {
      if (element.dataset.badge !== slug) element.dataset.badge = slug; // style with [data-badge="almost-gone"] etc.
    } else if (element.hasAttribute('data-badge')) {
      element.removeAttribute('data-badge');
    }

    const shouldHide = !badge;
    if (element.hidden !== shouldHide) element.hidden = shouldHide;
  };

  window.needohCartLineProofRefresh = () => {
    document.querySelectorAll(SELECTOR).forEach((element, index) => {
      applyBadge(element, pickBadge(element, index));
    });
  };

  // Re-run whenever cart lines are re-rendered (quantity change, remove, add to cart, etc.)
  let scheduled = false;
  const scheduleRefresh = () => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      window.needohCartLineProofRefresh();
    });
  };

  const containsBadge = (node) =>
    node.nodeType === 1 && (node.matches(SELECTOR) || node.querySelector(SELECTOR));

  new MutationObserver((mutations) => {
    if (mutations.some((mutation) => Array.from(mutation.addedNodes).some(containsBadge))) scheduleRefresh();
  }).observe(document.documentElement, { childList: true, subtree: true });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', window.needohCartLineProofRefresh, { once: true });
  } else {
    window.needohCartLineProofRefresh();
  }
})();
